/**
 * Model-output `*Draft` validators (law C4).
 *
 * Model / LLM output is the ONE legitimate trust boundary — it is VALIDATED,
 * never cast. Each `*Draft` is derived from a compiled domain type via ArkType
 * `.omit()`, dropping the server-stamped fields (ids, owner, confirmation status,
 * `PresentationMeta`, provenance, score math) the model must NOT author. The
 * derivation keeps ONE source of truth: the draft shape can never drift from the
 * full shape because it is projected from it.
 */

import { type } from "arktype"
import { compileDomain } from "./compiler"
import { PLAN_DIMENSION_KEYS } from "./value-circumplex"
import evidenceDomain from "./evidence.domain"
import opportunityDomain from "./opportunity.domain"
import organizationDomain from "./organization.domain"
import applicationDomain from "./application.domain"
import cvDomain from "./cv.domain"
import profileDomain from "./profile.domain"
import assetDomain from "./asset.domain"
import workshopDomain from "./workshop.domain"

const evidence = compileDomain(evidenceDomain).types
const opportunity = compileDomain(opportunityDomain).types
const organization = compileDomain(organizationDomain).types
const application = compileDomain(applicationDomain).types
const cv = compileDomain(cvDomain).types
const profile = compileDomain(profileDomain).types
const asset = compileDomain(assetDomain).types
const workshop = compileDomain(workshopDomain).types

/** The model proposes a claim; the server stamps `id`, `owner`, and the confirmation `status`. */
export const ClaimDraft = evidence.EvidenceClaim.omit("id", "owner", "status")
export type ClaimDraft = typeof ClaimDraft.infer

/** The model authors the opportunity brief; the server stamps `meta` (PresentationMeta). */
export const OpportunityBriefDraft = opportunity.OpportunityBrief.omit("meta")
export type OpportunityBriefDraft = typeof OpportunityBriefDraft.infer

// ─── Search-pipeline RAW-pass drafts (llm-workflows.md §4.3–4.7, slice 4) ──────
//
// The search pass is RAW → PRESENTATION. The RAW passes (verify, match, org-research,
// discovery-triage) validate model output against a `.omit()`-derived draft (law C4,
// validate-never-cast). The PRESENTATION passes (opportunity-brief, org-summary) ride
// `@platform/authoring` `runPresentation` — the runner validates its own `{body,
// provenance}` emission, so they need no draft here (the brief maps `body → brief`).

/**
 * The RAW verify verdict the model authors from the fully-scraped page — typed fields
 * ONLY, no prose (llm-workflows §4.3). The model authors EVERY VerifyVerdict field
 * (incl. the derived `register` read off the posting's own address form); the server
 * stamps nothing, so the draft IS the full verdict shape. Validated, never cast.
 */
export const VerifyVerdictDraft = opportunity.VerifyVerdict
export type VerifyVerdictDraft = typeof VerifyVerdictDraft.infer

/**
 * The RAW match signals the model authors (llm-workflows §4.5) — per-dimension
 * strengths + verbatim quotes + named gaps, the alignment dimensions covered, and the
 * anti-value hits. The DOMAIN owns ALL score math: `score` + `tier` (MatchTier) are
 * COMPUTED in code, NOT authored, so they are omitted here alongside the server-stamped
 * ids/prov/timestamps and the code-set `opportunityRef`. This is the ONLY match draft:
 * a `MatchDraft` that let the model author `score`+`tier` was retired (fix-round D3) so no
 * future caller can reintroduce model-authored scores.
 */
export const MatchSignalsDraft = opportunity.Match.omit(
  "id",
  "owner",
  "opportunityRef",
  "score",
  "tier",
  "prov",
  "createdAt",
)
export type MatchSignalsDraft = typeof MatchSignalsDraft.infer

/**
 * The RAW org-research bundle the model authors (llm-workflows §4.4) — typed evidence +
 * sources, plus the derived org `register` when observable. The server stamps the orgId /
 * timestamps; the presented German summary is a SEPARATE presentation pass.
 */
export const OrgResearchDraft = type({
  evidence: organization.OrgEvidence.array(),
  "register?": "'du' | 'Sie'",
})
export type OrgResearchDraft = typeof OrgResearchDraft.infer

/**
 * The RAW discovery-triage verdict (llm-workflows §4.7) — the model triages the raw SERP
 * hits, keeping the postings worth the (costly) scrape+verify and dropping obvious junk
 * (aggregators, dead links, off-target). English prompt, English fields. One entry per
 * candidate posting, addressed by its index in the batch.
 */
export const DiscoveryTriageDraft = type({
  items: type({
    index: "number.integer >= 0",
    keep: "boolean",
    "reason?": "string",
  }).array(),
})
export type DiscoveryTriageDraft = typeof DiscoveryTriageDraft.infer

/** The model authors the cover letter; the server stamps `meta` (PresentationMeta). */
export const CoverLetterDraft = application.CoverLetter.omit("meta")
export type CoverLetterDraft = typeof CoverLetterDraft.infer

/** The model authors the per-opportunity Kurzprofil; the server stamps `meta` (PresentationMeta).
 *  Same RAW → PRESENTATION discipline as the cover letter (llm-workflows §4.6, parity with appgen P1). */
export const KurzprofilDraft = application.Kurzprofil.omit("meta")
export type KurzprofilDraft = typeof KurzprofilDraft.infer

/**
 * The RAW `preselect` bundle the model authors (llm-workflows §4.6): the ANGLE (the "what to say"
 * decision), the lead-claim URNs to anchor the letter, and the curated attachments + smart links
 * (each user-toggled later). The server stamps the code-pinned fields the model must NOT author —
 * `contextRefs` (pinned by the server for reproducibility), `cvVersion` (the current Cv version), and
 * the register (read off the opportunity, never authored) — so they are omitted here. Validated,
 * never cast (law C4); refs reconciled against the real inventory before persistence.
 */
export const ApplicationPreselectDraft = application.ApplicationRawBundle.omit("contextRefs", "cvVersion", "register")
export type ApplicationPreselectDraft = typeof ApplicationPreselectDraft.infer

// ─── Ingest / extraction RAW-pass drafts (llm-workflows.md §4.1) ─────────────
//
// The extraction pass emits ONE structured object (`ExtractionDraft`): graded
// claim proposals (verbatim sources), a draft Cv, a profile seed, and asset
// classification proposals. Each part is `.omit()`-derived from its compiled
// def so the draft shape can never drift from the full shape (law C4). English
// internal prose (the German Kandidatenprofil is authored later, in the
// presentation pass); the model never fabricates — a missing beat is a gap.

/** The model authors the structured Cv from the parsed documents; the server stamps
 *  `id`, `owner`, `version`, and the timestamps. */
export const CvDraft = cv.Cv.omit("id", "owner", "version", "createdAt", "updatedAt")
export type CvDraft = typeof CvDraft.infer

/** The model authors the first structured profile seed; the server stamps `owner`. */
export const ProfileSeedDraft = profile.CandidateProfile.omit("owner")
export type ProfileSeedDraft = typeof ProfileSeedDraft.infer

/** The model proposes a typed asset classification for an uploaded document; the
 *  server stamps `id`, `owner`, `status` (proposed), `version`, `file` (the
 *  document's reused ObjectRef), `evidenceClaims`, and the timestamps. */
export const AssetProposalDraft = asset.Asset.omit(
  "id",
  "owner",
  "status",
  "version",
  "file",
  "evidenceClaims",
  "createdAt",
  "updatedAt",
)
export type AssetProposalDraft = typeof AssetProposalDraft.infer

/**
 * The whole RAW extraction output — one structured call. Composed from the
 * `.omit()`-derived part drafts (no re-authoring), so validation stays a single
 * source of truth. The api validates the model reply against THIS, never casts.
 */
export const ExtractionDraft = type({
  claims: ClaimDraft.array(),
  cv: CvDraft,
  profile: ProfileSeedDraft,
  assets: AssetProposalDraft.array(),
})
export type ExtractionDraft = typeof ExtractionDraft.infer

// ─── Workshop probe stimulus draft (ADR-013 / llm-workflows.md §4.2) ─────────
//
// The adaptive interviewer (`makeLlmProbeProvider`) returns ONE structured move
// per step: an adapted recognition CARD, or a `stepSufficient` skip, plus the
// candidate-facing `rationale` (the visible voice). The model authors the card
// BODY — which now also carries the DISCLOSED `denkweise` voice (the "Überlegung",
// ADR-016) — and the handler stamps the `id` (the step id) and attaches the
// top-level `rationale`. Two DISTINCT voices per card (FR-4.6 / FR-4.7): `rationale`
// is the short warm read shown by default; `denkweise` is the "why I asked" the
// candidate expands on demand. This draft is the TRUST BOUNDARY the probe's injected
// `validate` asserts — derived `.omit()` from the compiled ElicitationCard so it
// can never drift.

/** The card BODY the model authors — `id` is the step id (handler-owned) and the
 *  top-level `rationale` rides on the decision; the disclosed `denkweise` voice
 *  is authored INSIDE the card body (so it survives elicitation-core's `...body`
 *  assembly untouched — the platform provider stamps only `rationale`).
 *
 *  Two further model-output rules deliberately do NOT intersect into this draft —
 *  per-primitive structure (`cardStructuralGap`) and per-field TEXT BUDGETS
 *  (`CardTextBudget` / `cardBudgetGap`, the UI slot each string is written into).
 *  Both are real validators, but they run in the probe's grade-and-REGENERATE loop:
 *  folding them in here would throw at the probe's `validate`, and `makeLlmProbeProvider`
 *  falls straight to the synthetic seed on a `validate` throw — losing the retries that
 *  are exactly how a model fixes an over-long or mis-shaped card. */
export const ProbeCardDraft = workshop.ElicitationCard.omit("id", "rationale")
export type ProbeCardDraft = typeof ProbeCardDraft.infer

/** One interviewer move: an adapted card OR a `stepSufficient` skip, always with
 *  a `rationale`. Validated (never cast) at the probe's trust boundary. */
export const ProbeStimulusDraft = type({
  "card?": ProbeCardDraft,
  rationale: "string > 0",
  "stepSufficient?": "boolean",
})
export type ProbeStimulusDraft = typeof ProbeStimulusDraft.infer

// ─── Runden-Planner draft (W1 redesign §4, V3 — the round-plan trust boundary) ──
//
// The planner authors the whole plan (rounds + blocks); the server stamps `plannedAt`
// (a server timestamp the model must not author). `.omit()`-derived from the compiled
// `RoundPlan` so the draft can never drift from the full shape (law C4). A malformed
// plan throws at `.assert` → the deterministic planner floor engages (§4).

/** The model authors the round plan; the server stamps `plannedAt`. Validated, never cast. */
export const RoundPlanDraft = workshop.RoundPlan.omit("plannedAt")
export type RoundPlanDraft = typeof RoundPlanDraft.infer

/**
 * The revision pass (§4, V3.1 — "revision after each completed epic") re-reads the plan +
 * every answer + the coverage state and either CONFIRMS the remaining rounds (`changed:
 * false`) or AMENDS them: it returns ONLY the amended REMAINING rounds (may be empty) plus
 * a short candidate-facing German `reason`. The server composes the full plan (completed
 * rounds preserved verbatim), so completed rounds can never be rewritten. Validated, never
 * cast (law C4) — a malformed revision throws → the current plan is kept unchanged.
 */
export const PlanRevisionDraft = type({
  changed: "boolean",
  /** The amended remaining rounds (the model omits/keeps as it sees fit); ignored when `changed:false`. */
  rounds: workshop.RoundPlanRound.array(),
  /** Short candidate-facing German reason for the amendment (shown as »Plan angepasst«). */
  reason: "string",
})
export type PlanRevisionDraft = typeof PlanRevisionDraft.infer

/**
 * The planner-seat EMISSION types — the same shapes as the two drafts above with ONE difference:
 * `dimension` is the ENUM of canonical model keys rather than a free `NonEmptyString`.
 *
 * Why a second pair rather than narrowing the drafts (2026-07-30, the `self_direction` incident):
 * the allowed dimension set used to live ONLY in prose, so the contract was an instruction a model
 * could miss — and one did, echoing the Schwartz VALUE-level key `self_direction` out of our own
 * data lines into a field whose legal values are the four HIGHER-ORDER keys. These types are what
 * the wire `responseSchema` is derived from, so the allowed set is now STRUCTURAL: a strict
 * provider cannot emit an illegal token at all.
 *
 * They are deliberately NOT the validators. Reading is still tolerant (`RoundPlanDraft` +
 * `normalizeDimension`, which maps a German label or a value-level key home) because non-strict
 * providers exist and a recoverable token must not cost the whole plan — the enum is what we ASK
 * for, the normalizer is the net behind it. And because `dimension` stays `NonEmptyString` on the
 * STORED `RoundPlanBlock`, no event schema moves: every fold and golden replay is untouched.
 */
export const PlanDimension = type.enumerated(...PLAN_DIMENSION_KEYS)
export type PlanDimension = typeof PlanDimension.infer

// `.merge` rebuilds the object and drops the source's `"+": "reject"`, so each merged level
// re-declares it — an emission schema that silently allowed undeclared keys would be LOOSER than
// the draft it stands in for, which is the one direction a model-facing contract must never move.
const RoundPlanBlockEmission = workshop.RoundPlanBlock.merge({ dimension: PlanDimension }).onUndeclaredKey("reject")
const RoundPlanRoundEmission = workshop.RoundPlanRound.merge({ blocks: RoundPlanBlockEmission.array() }).onUndeclaredKey("reject")

/** `RoundPlanDraft` with the `dimension` enum — the schema the planner call is constrained to. */
export const RoundPlanEmission = RoundPlanDraft.merge({ rounds: RoundPlanRoundEmission.array() }).onUndeclaredKey("reject")
export type RoundPlanEmission = typeof RoundPlanEmission.infer

/** `PlanRevisionDraft` with the same enum — the revision call shares the planner's contract. */
export const PlanRevisionEmission = PlanRevisionDraft.merge({ rounds: RoundPlanRoundEmission.array() })
export type PlanRevisionEmission = typeof PlanRevisionEmission.infer

// ─── Schwartz prognosis draft (W1 redesign §5c, V4.2 — the circumplex reward) ──
//
// The model REFINES a per-basic-value magnitude + confidence and authors the German
// `insight`; the sector STRUCTURE (which ten values, their labels, the circumplex
// order, the higher-order region) is composed in CODE from the pyramid ranking + the
// tagged interview evidence (`value-model.ts`) — the same score-math discipline that
// omits `score`/`tier` from `MatchSignalsDraft`. The server reconciles: each authored
// `value` maps to a canonical basic value (unknown → dropped), missing sectors fall
// back to the deterministic default, and the whole set is re-ordered + re-labelled from
// the circumplex vocabulary. Validated, never cast (law C4) — a malformed prognosis
// throws → the deterministic default profile.

/** One model-refined sector reading — just the numbers + which value (code owns the rest). */
export const SchwartzSectorDraft = type({
  value: "string > 0",
  magnitude: "0 <= number <= 1",
  confidence: "0 <= number <= 1",
})
export type SchwartzSectorDraft = typeof SchwartzSectorDraft.infer

/**
 * WHERE ONE OF THE PERSON'S OWN CARDS BELONGS, for this person.
 *
 * The catalog annotates each card with one primary Schwartz value, and that mapping is the
 * measured weak point of the whole surface: it renders a real user's »Zugehörigkeit« as
 * `security`, when what he meant was being loved for who he is. Which sector a multivalent
 * word belongs to is a per-person judgement, so the model is asked for it.
 *
 * It is NOT trusted from one model. A single model placing its own cards would be able to
 * move its own guardrail, which is the reason this was kept out of the guard in the first
 * place. It is used only where readings AGREE (`cardPlacementConsensus`), and the catalog
 * decides whenever they do not.
 */
export const SchwartzCardPlacementDraft = type({
  /** The German word on the card, as it was shown to the person. */
  card: "string > 0",
  /** The basic value the model reads that word as, for THIS person. */
  value: "string > 0",
})
export type SchwartzCardPlacementDraft = typeof SchwartzCardPlacementDraft.infer

/**
 * The model authors per-sector numbers + the German insight; code composes the profile.
 * `placements` is additive and OPTIONAL — a draft written before it existed still validates,
 * and a prediction without it simply leaves the catalog as the guard's arbiter.
 */
export const SchwartzPrognosisDraft = type({
  sectors: SchwartzSectorDraft.array(),
  insight: "string",
  "placements?": SchwartzCardPlacementDraft.array(),
})
export type SchwartzPrognosisDraft = typeof SchwartzPrognosisDraft.infer

// ─── Werte-Sektion drafts (W1 redesign §6, V1 — validate future model output) ──
//
// V1 adds NO seam yet; these are the trust-boundary validators the Phase-3
// analysis (V5, `workshop_enrich`) will assert against — authored now with the
// types so they can never drift. `.omit()`-derived from the shared-spine shapes
// (resolvable off any compiled scope; the `workshop` module is the anchor).

/**
 * The model authors the statement TEXTS; the per-framework `readouts` (coverage +
 * confidence) are COMPOSED in code from the weighting heuristics (§5a), never
 * authored — the same score-math discipline that omits `score`/`tier` from
 * `MatchSignalsDraft`. Validated, never cast (law C4).
 */
export const PersonalStatementsDraft = workshop.PersonalStatements.omit("readouts")
export type PersonalStatementsDraft = typeof PersonalStatementsDraft.infer

/**
 * The model authors a value entry's DETAIL (definition/leitsatz/examples/story/
 * tags/name); `cardId` + `tier` come from the committed pyramid slot (§3), not the
 * model, so they are omitted here. `definition`/`leitsatz` stay optional — the top-4
 * carry them, the Basiswerte ship selected-with-tags (Q5).
 */
export const PersonalValueEntryDraft = workshop.PersonalValueEntry.omit("cardId", "tier")
export type PersonalValueEntryDraft = typeof PersonalValueEntryDraft.infer

/**
 * The whole Phase-3 analysis output — the enriched per-value detail + the four
 * statements. Composed from the `.omit()`-derived part drafts (one source of
 * truth, mirrors `ExtractionDraft`); the api reconciles `cardId`/`tier` against the
 * committed pyramid before persistence.
 */
export const WerteSektionDraft = type({
  values: PersonalValueEntryDraft.array(),
  statements: PersonalStatementsDraft,
})
export type WerteSektionDraft = typeof WerteSektionDraft.infer

/**
 * The refine pass rewrites ONE text block (a statement, or a value's definition/
 * leitsatz) against the user's feedback — the model returns just the revised
 * `proposal`. It never replaces the original silently: the user accepts it explicitly
 * (HITL). Validated, never cast (law C4).
 */
export const WerteSektionRefineDraft = type({ proposal: "string > 0" })
export type WerteSektionRefineDraft = typeof WerteSektionRefineDraft.infer

// ─── Search-profile draft (W2 »Was ich suche« — DERIVE seam trust boundary) ────
//
// W2's Suchprofil was entirely deterministic: it lifted CV job titles verbatim and
// no model ran. The DERIVE seam predicts role TITLES that cover the person's unique
// value propositions (Schwartz higher-order profile + personal statements + value
// readouts + CV titles + free-text intent), so the person is offered roles they may
// not have named themselves. Model output is the ONE trust boundary — validated with
// this draft (never cast, law C4). Hand-authored (like `OrgResearchDraft` /
// `DiscoveryTriageDraft`): there is no single domain type carrying role-with-rationale,
// so this cannot be `.omit()`-projected from one. The proposal is transient — the user
// confirms it into a `SearchConfig` via the existing `saveSearchProfile`, so nothing
// here is persisted directly and no domain shape can drift from it.

/** One proposed role: the search TERM and a one-sentence German rationale grounding it
 *  in THIS person's values/evidence (never a generic justification). */
export const SearchProfileRoleDraft = type({
  term: "string > 0",
  rationale: "string > 0",
})
export type SearchProfileRoleDraft = typeof SearchProfileRoleDraft.infer

/**
 * The whole derived search profile the model authors: roles-with-rationale plus the
 * seniority / location / exclusion lists and a warm human-facing German `summary` of
 * what we understood the person is looking for. `summary` may be empty (a thin-but-honest
 * read beats an invented one); the lists may be empty. Validated, never cast (law C4).
 */
export const SearchProfileDraft = type({
  roles: SearchProfileRoleDraft.array(),
  seniority: "string[]",
  locations: "string[]",
  excludeKeywords: "string[]",
  summary: "string",
})
export type SearchProfileDraft = typeof SearchProfileDraft.infer

// ─── W4 »Schreibstil« drafts (writing_style — the voice track's trust boundaries) ──
//
// Four AI passes, four drafts. Each is `.omit()`-derived from the compiled W4 shape
// wherever one exists, so a draft can never drift from the type it feeds (law C4).
// The split is always the same: the MODEL authors prose; CODE owns everything a model
// must not be able to move — the asset `version`, the server `derivedAt`, the per-trait
// `basis` (which rounds a reading actually rests on), and above all `samples`, which are
// the person's own verbatim text. A model that could rewrite the samples could rewrite
// the evidence for its own conclusions.

/** Round ① — the model authors the whole bullet set (brief + bullets + grounding); the
 *  server stamps nothing, so the draft IS the full shape. Validated, never cast. */
export const WritingBulletSetDraft = workshop.WritingBulletSet
export type WritingBulletSetDraft = typeof WritingBulletSetDraft.infer

/** Round ② — the model authors the whole scenario SET (situations, groundings, and the
 *  same-content variants). Authored as an array wrapper rather than a bare array so the
 *  pass returns ONE structured object, like every other seam here. */
export const WritingScenariosDraft = type({
  scenarios: workshop.WritingScenario.array(),
})
export type WritingScenariosDraft = typeof WritingScenariosDraft.infer

/** Round ③ — the model writes ONE text in the derived style plus the German `context`
 *  naming what it was written for. Hand-authored (like `OrgResearchDraft`): the round-③
 *  event carries these two fields alongside code-owned provenance, and there is no single
 *  domain type holding just the authored half. */
export const WritingValidationDraft = type({
  text: "string > 0",
  context: "string",
})
export type WritingValidationDraft = typeof WritingValidationDraft.infer

/**
 * The ASSET derivation — the model authors the German `summary`, the trait axes and
 * readings, the `guidance` DO-instructions and the `avoid` list, and PROPOSES a
 * confidence (which code clamps to what the rounds actually ran — round ① alone can
 * never yield a high one). Code owns `version` (previous + 1), `derivedAt`, `locale`,
 * the observed `register`, each trait's `basis`, and `samples` (verbatim user text).
 * Traits are therefore re-authored here minus `basis` rather than `.omit()`-projected
 * whole, because the omission is INSIDE the array element, not at the top level.
 */
export const WritingStyleTraitDraft = workshop.WritingStyleTrait.omit("basis")
export type WritingStyleTraitDraft = typeof WritingStyleTraitDraft.infer

export const WritingStyleProfileDraft = type({
  summary: "string > 0",
  traits: WritingStyleTraitDraft.array(),
  guidance: "string[]",
  avoid: "string[]",
  /** The model's PROPOSED overall confidence — clamped by code to the rounds that ran. */
  confidence: "0 <= number <= 1",
})
export type WritingStyleProfileDraft = typeof WritingStyleProfileDraft.infer
